import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	useHistory,
	useLocation
} from 'react-router';
import {
	Divider,
	Form,
	Icon,
	Image,
	Label,
	Message,
	Tab
} from 'semantic-ui-react';

import { stateOptions } from '../../../../../services/constants';
import {
	log,
	print
} from '../../../../../services/utils';

import { updateUserPhoto } from '../../../../../api/nora/users';
import { uploadAsset } from '../../../../../api/nora/assets';

import { useAuthState } from '../../../../../contexts/AuthContext';
import { useAdminState } from '../../../../../contexts/AdminContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import Actions from '../../../../Shared/Actions';
import Alert from '../../../../Alert';
import DDFileUploadModal from '../../../../DDFileUpload';
import FormCheckbox from '../../../../Form/FormCheckbox';
import FormInput from '../../../../Form/FormInput';
import FormPassword from '../../../../Form/FormPassword';
import FormSelect from '../../../../Form/FormSelect';
import FormErrors from '../../../../Shared/FormErrors';

const InfoTab = ( {
	errors,
	forceValidation,
	formData,
	formDataHasChanged,
	handleSave,
	resetFormData,
	updateField,
	validate
} ) => {
	const history = useHistory();
	const { state: navigationState } = useLocation();

	const { user: { permissions } } = useAuthState();
	const {
		agencyOrganizations,
		managementOrganizations,
		userRoles
	} = useAdminState();
	const { setApiErrorAlert } = useUIAction();

	const [ editable, setEditable ] = useState( !formData.id );
	const [ fileUploadModal, setFileUploadModal ] = useState( false );
	const [ organizationOptions, setOrganizationOptions ] = useState( [] );
	const [ removePhotoConfirmAlert, setRemovePhotoConfirmAlert ] = useState( false );
	const [ isSaving, setIsSaving ] = useState( false );

	useEffect( () => {
		if ( formData.role ) {
			switch ( formData.role ) {
				case 1: // system administrator
				case 4: // program manager
				case 5: // contact manager
				case 7: // support administrator
					setOrganizationOptions( managementOrganizations );
					break;
				case 2: // agency administrator
				case 6: // agency user
					setOrganizationOptions( agencyOrganizations );
					break;
				default:
					log( 'invalid organization type' );
					setOrganizationOptions( [] );
					break;
			}
		}
	}, [
		agencyOrganizations,
		formData.role,
		formData.type,
		managementOrganizations
	] );

	const mapFormErrors = useCallback(
		() => Object.values( errors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ errors ]
	);

	const save = useCallback( async () => {
		const cb = () => {
			if ( !validate() ) {
				setEditable( false );
			}
		};

		setIsSaving( true );
		await handleSave( cb );
		setIsSaving( false );
	}, [
		handleSave,
		validate
	] );

	const saveAction = useMemo(
		() => ( {
			action: save,
			disable: !formDataHasChanged || isSaving,
			icon: 'save',
			permission: true,
			text: formData.id === 0
				? 'Save As New User'
				: 'Save'
		} ),
		[
			formData.id,
			formDataHasChanged,
			isSaving,
			save
		]
	);

	const cancel = useCallback( () => {
		resetFormData();

		if ( formData.id === 0 ) {
			if ( navigationState?.to ) {
				history.push( navigationState.to );
			} else {
				history.push( '/administration/users' );
			}
		} else {
			setEditable( false );
		}
	}, [
		formData.id,
		history,
		navigationState,
		resetFormData
	] );

	const cancelAction = useMemo(
		() => ( {
			action: cancel,
			icon: 'times',
			permission: true,
			text: 'Cancel'
		} ),
		[ cancel ]
	);

	const edit = useCallback( () => {
		setEditable( true );
	}, [] );

	const editAction = useMemo( () => ( {
		action: edit,
		disable: !permissions.editUsers.canExecute,
		icon: 'pencil',
		permission: permissions.editUsers.isAvailable,
		text: 'Edit'
	} ), [
		edit,
		permissions
	] );

	const printAction = useMemo( () => ( {
		action: print,
		disable: validate(),
		icon: 'print',
		permission: true,
		text: 'Print'
	} ), [ validate ] );

	const actions = useMemo(
		() => editable
			? [
				cancelAction,
				saveAction
			]
			: [
				printAction,
				editAction
			],
		[
			cancelAction,
			editable,
			saveAction,
			printAction,
			editAction
		]
	);

	const handleUploadPhoto = useCallback( () => {
		setFileUploadModal( true );
	}, [] );

	const onUpload = useCallback( async ( { uid } ) => {
		if ( formData.id ) {
			try {
				const response = await updateUserPhoto( formData.id, uid );

				updateField( {
					target: {
						name: 'photo',
						value: response.uri
					}
				}, true );
			} catch ( err ) {
				setApiErrorAlert( err );
			}
		}
	}, [
		formData.id,
		setApiErrorAlert,
		updateField
	] );

	const handleRemovePhoto = useCallback( () => {
		setRemovePhotoConfirmAlert( true );
	}, [] );

	const removePhoto = useCallback( () => {
		if ( formData.id ) {
			try {
				updateUserPhoto( formData.id, 0 );

				updateField(
					{
						target: {
							name: 'photo',
							value: null
						}
					},
					true
				);
			} catch ( err ) {
				setApiErrorAlert( err );
			}
		}
	}, [
		formData.id,
		setApiErrorAlert,
		updateField
	] );

	const closeFileUploadModal = useCallback( () => {
		setFileUploadModal( false );
	}, [] );

	const closeRemovePhotoConfirmAlert = useCallback( () => {
		setRemovePhotoConfirmAlert( false );
	}, [] );

	// organizations
	const handleUserRoleChange = useCallback( e => {
		updateField( e ); // change type
		updateField( {
			target: {
				name: 'organization',
				value: e.target.value === 1 /* admin */
					? 1 /* IDOH */
					: ''
			}
		} ); // set organization
	}, [ updateField ] );

	const emailIcon = useMemo(
		() => !errors.email.some( error => error.error )
			? <Icon
				color="black"
				link
				name="mail"
				onClick={ () => console.log( 'email contact' ) }
			/>
			: null
		, [ errors.email ]
	);

	const countryCode = useMemo( () => <Label basic><span>+1&nbsp;</span></Label>, [] );

	return (
		<Tab.Pane as="div">
			{ log( 'InfoTab' ) }
			<Actions actions={ actions } />
			<div className="contact-form-container">
				<div className="no-print">
					<Image
						size="small"
						src={ formData.photo
							? formData.photo
							: 'https://react.semantic-ui.com/images/wireframe/image.png'
						}
					/>
					{ editable
						? formData.photo
							? <small className="image-upload-actions">
								<span onClick={ handleUploadPhoto }>edit</span>&nbsp;&nbsp;|&nbsp;&nbsp;<span onClick={ handleRemovePhoto }>remove</span>
							</small>
							: <small className="image-upload-actions">
								<span onClick={ handleUploadPhoto }>upload</span>
							</small>
						: null
					}
				</div>

				<Form autoComplete="off" error={ forceValidation && validate() }>
					<input
						className="hidden-input"
						name="city"
					/>

					<Form.Group>
						<FormSelect
							errors={ errors.role }
							fieldLabel="Role"
							forceValidation={ forceValidation }
							helpId={ editable ? 32 : null }
							name="role"
							onChange={ handleUserRoleChange }
							options={ userRoles }
							readOnly={ !editable }
							required
							value={ formData.role || '' }
							width={ 6 }
						/>
					</Form.Group>

					<Form.Group>
						<FormInput
							errors={ errors.firstName }
							fieldLabel="First Name"
							forceValidation={ forceValidation }
							maxLength={ 255 }
							name="firstName"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.firstName || '' }
							width={ 6 }
						/>
						<FormInput
							errors={ errors.lastName }
							fieldLabel="Last Name"
							forceValidation={ forceValidation }
							maxLength={ 255 }
							name="lastName"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.lastName || '' }
							width={ 6 }
						/>
					</Form.Group>

					<Form.Group>
						<FormInput
							errors={ errors.userName }
							fieldLabel="Username"
							forceValidation={ forceValidation }
							maxLength={ 255 }
							name="userName"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.userName || '' }
							width={ 6 }
						/>
						<FormSelect
							errors={ errors.organization }
							fieldLabel="Organization"
							forceValidation={ forceValidation }
							name="organization"
							onChange={ updateField }
							options={ organizationOptions } // will need options
							readOnly={ !editable }
							required
							search
							value={ formData.organization || '' }
							width={ 6 }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormInput
							fieldLabel="Address"
							name="address"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.address || '' }
							width={ 10 }
						/>
					</Form.Group>

					<Form.Group>
						<FormInput
							fieldLabel="Address Line 2"
							name="address2"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.address2 || '' }
							width={ 10 }
						/>
					</Form.Group>

					<Form.Group>
						<FormInput
							fieldLabel="City"
							name="city"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.city || '' }
							width={ 6 }
						/>
						<FormSelect
							fieldLabel="State"
							maxLength={ 2 }
							name="state"
							onChange={ updateField }
							options={ stateOptions }
							readOnly={ !editable }
							search
							value={ formData.state || '' }
							width={ 2 }
						/>
						<FormInput
							errors={ errors.zip }
							fieldLabel="Zip"
							forceValidation={ forceValidation }
							keyDown="integer"
							maxLength={ 5 }
							name="zip"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.zip || '' }
							width={ 2 }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormInput
							errors={ errors.email }
							fieldLabel="Email"
							forceValidation={ forceValidation }
							icon={ emailIcon }
							maxLength={ 254 }
							name="email"
							readOnly={ !editable }
							required
							value={ formData.email || '' }
							onChange={ updateField }
							width={ 6 }
						/>
					</Form.Group>

					<Form.Group>
						<FormInput
							errors={ errors.phone }
							fieldLabel="Phone"
							forceValidation={ forceValidation }
							keyDown="integer"
							inputLabel={ countryCode }
							mask="phone"
							maxLength={ 14 }
							name="phone"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.phone || '' }
							width={ 6 }
						/>
						<FormInput
							fieldLabel="Ext."
							forceValidation={ forceValidation }
							keyDown="integer"
							name="ext"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.ext || '' }
							width={ 2 }
						/>
						<FormCheckbox
							checked={ formData.SMSEnabled }
							fieldLabel="SMS Enabled"
							name="SMSEnabled"
							onChange={ updateField }
							readOnly={ !editable }
							width={ 3 }
						/>
					</Form.Group>

					{ formData.id === 0
						? <>
							<Divider
								hidden
								style={ { margin: '1.5rem 0' } }
							/>
							<Form.Group>
								<FormPassword
									errors={ errors.pass }
									fieldLabel="Password"
									forceValidation={ forceValidation }
									maxLength={ 65 }
									name="pass"
									onChange={ updateField }
									required
									value={ formData.pass || '' }
									width={ 6 }
								/>
							</Form.Group>
							<Form.Group>
								<FormPassword
									errors={ errors.confirm }
									fieldLabel="Confirm Password"
									forceValidation={ forceValidation }
									maxLength={ 65 }
									name="confirm"
									onChange={ updateField }
									required
									value={ formData.confirm || '' }
									width={ 6 }
								/>
							</Form.Group>
						</>
						: null
					}
					<FormErrors mapFormErrors={ mapFormErrors } />
				</Form>
			</div>
			<DDFileUploadModal
				closeModal={ closeFileUploadModal }
				onUpload={ onUpload }
				open={ fileUploadModal }
				upload={ uploadAsset }
			/>
			<Alert
				action={ removePhoto }
				actionText="Remove Photo"
				name="admin-remove-photo-confirm-alert"
				message="Are you sure?"
				open={ removePhotoConfirmAlert }
				closeModal={ closeRemovePhotoConfirmAlert }
				closeText="Cancel"
				type="confirm"
			/>
		</Tab.Pane>
	);
};

InfoTab.propTypes = {
	errors: PropTypes.object,
	forceValidation: PropTypes.bool,
	formData: PropTypes.object,
	formDataHasChanged: PropTypes.bool,
	handleSave: PropTypes.func,
	resetFormData: PropTypes.func,
	updateField: PropTypes.func,
	validate: PropTypes.func
};

export default memo( InfoTab );
